import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { JobNoteWithUser, JobNoteInput } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface JobNotesResponse {
  notes: JobNoteWithUser[];
}

interface JobNoteResponse {
  note: JobNoteWithUser;
}

// Query keys
export const jobNoteKeys = {
  all: ['jobNotes'] as const,
  lists: () => [...jobNoteKeys.all, 'list'] as const,
  list: (jobId: string) => [...jobNoteKeys.lists(), jobId] as const,
};

// Fetch all notes for a specific job
export function useJobNotes(jobId: string | undefined) {
  return useQuery({
    queryKey: jobNoteKeys.list(jobId!),
    queryFn: async () => {
      const response = await apiClient.get<JobNotesResponse>('/job-notes', {
        params: { jobId },
      });
      return response.notes;
    },
    enabled: !!jobId,
  });
}

// Create job note mutation
export function useCreateJobNote() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: JobNoteInput) => {
      const response = await apiClient.post<JobNoteResponse>('/job-notes', data);
      return response.note;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch notes for this job
      queryClient.invalidateQueries({ queryKey: jobNoteKeys.list(variables.jobId) });

      toast({
        title: 'Note added',
        description: 'Your note has been added successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add note. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// Update job note mutation
export function useUpdateJobNote(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { id: string; message: string }) => {
      const response = await apiClient.put<JobNoteResponse>(`/job-notes/${data.id}`, {
        message: data.message,
      });
      return response.note;
    },
    onSuccess: () => {
      // Invalidate and refetch notes for this job
      queryClient.invalidateQueries({ queryKey: jobNoteKeys.list(jobId) });

      toast({
        title: 'Note updated',
        description: 'Your note has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update note. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// Delete job note mutation
export function useDeleteJobNote(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/job-notes/${id}`);
    },
    onSuccess: () => {
      // Invalidate and refetch notes for this job
      queryClient.invalidateQueries({ queryKey: jobNoteKeys.list(jobId) });

      toast({
        title: 'Note deleted',
        description: 'Your note has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete note. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
